import React, { useState, useEffect, useRef, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Send, Paperclip, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWebSocket } from "@/context/WebSocketContext";
import { useSearchParams } from "react-router-dom";

import RichTextEditor, { RichTextDisplay } from "@/components/RichTextEditor";
function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  return isToday
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "numeric", month: "short" });
}

// ── Conversation pane ─────────────────────────────────────────────────────────
function Conversation({ thread, onDelete }) {
  const { subscribe, send } = useWebSocket();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [deletingThread, setDeletingThread] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypeSentRef = useRef(0);

  useEffect(() => {
    setLoading(true);
    api.get(`/threads/${thread.id}/messages`)
      .then((r) => setMessages(r.data))
      .finally(() => setLoading(false));
  }, [thread.id]);

  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.thread_id === thread.id && event.sender_type === "client") {
        api.get(`/threads/${thread.id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
      }
      if (event.type === "typing" && event.thread_id === thread.id && event.user_id !== user?.id) {
        setTypingUser(event.user_name);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingUser(null), 3000);
      }
    });
    return unsub;
  }, [subscribe, thread.id, user?.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFile = (e) => {
    Array.from(e.target.files || []).forEach((file) => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} too large`); return; }
      const r = new FileReader();
      r.onload = (ev) => {
        const b64 = ev.target.result.split(",")[1];
        setAttachments((p) => [...p, { filename: file.name, content_type: file.type, data_b64: b64 }]);
      };
      r.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const handleSend = async () => {
    if (!content.replace(/<[^>]*>/g, "").trim() && attachments.length === 0) return;
    setSending(true);
    try {
      const r = await api.post(`/threads/${thread.id}/messages`, { content, attachments });
      setMessages((p) => [...p, r.data]);
      setContent("");
      setAttachments([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteThread = async () => {
    try {
      await api.delete(`/threads/${thread.id}`);
      toast.success("Conversation deleted");
      onDelete(thread.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeletingThread(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="px-5 py-3.5 border-b flex items-start justify-between flex-shrink-0" style={{ borderColor: "#E7E5E4" }}>
        <div>
          <p className="font-semibold text-sm" style={{ color: "#292524" }}>{thread.subject}</p>
          <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>
            {thread.client_name || thread.client_id}
            {thread.related_request_title && <span> · {thread.related_request_title}</span>}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {deletingThread ? (
            <>
              <button onClick={handleDeleteThread} className="text-xs px-2.5 py-1.5 rounded-lg font-medium" style={{ background: "#FEE2E2", color: "#DC2626" }}>Delete</button>
              <button onClick={() => setDeletingThread(false)} className="text-xs px-2 py-1.5 rounded-lg" style={{ background: "#F5F3EF", color: "#78716C" }}>Cancel</button>
            </>
          ) : (
            <button onClick={() => setDeletingThread(true)} className="p-1.5 rounded-lg hover:bg-stone-100" style={{ color: "#C7C3BF" }} title="Delete conversation">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {loading ? (
          <p className="text-xs text-center" style={{ color: "#A8A29E" }}>Loading...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-center" style={{ color: "#A8A29E" }}>No messages yet. Send the first one.</p>
        ) : messages.map((msg) => {
          const isAgency = msg.sender_type === "agency";
          return (
            <div key={msg.id} className={`flex ${isAgency ? "justify-end" : "justify-start"}`}>
              <div className="max-w-[75%]">
                <div className={`flex items-center gap-1.5 mb-1 ${isAgency ? "justify-end" : ""}`}>
                  <span className="text-xs font-medium" style={{ color: "#57534E" }}>{msg.sender_name}</span>
                  <span className="text-xs" style={{ color: "#C7C3BF" }}>{fmt(msg.created_at)}</span>
                </div>
                <div className="px-4 py-2.5 rounded-2xl text-sm" style={{
                  background: isAgency ? "#292524" : "#F5F3EF",
                  color: isAgency ? "#FAFAF9" : "#292524",
                  borderBottomRightRadius: isAgency ? 4 : undefined,
                  borderBottomLeftRadius: isAgency ? undefined : 4,
                }}>
                  <RichTextDisplay content={msg.content} />
                </div>
                {(msg.attachments || []).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5 justify-end">
                    {msg.attachments.map((a, i) => (
                      <a key={i} href={`/api/threads/attachments/${a.id}`} target="_blank" rel="noreferrer"
                        className="text-xs px-2.5 py-1 rounded-lg flex items-center gap-1" style={{ background: "#F5F3EF", color: "#57534E" }}>
                        <Paperclip size={10} />{a.filename}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {typingUser && (
          <div className="flex items-center gap-2 text-xs" style={{ color: "#A8A29E" }}>
            <div className="flex gap-0.5 items-center">
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "#C7C3BF", animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "#C7C3BF", animationDelay: "150ms" }} />
              <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: "#C7C3BF", animationDelay: "300ms" }} />
            </div>
            <span>{typingUser} is typing...</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="px-5 py-2 flex flex-wrap gap-2 border-t" style={{ borderColor: "#F5F3EF" }}>
          {attachments.map((a, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full" style={{ background: "#F5F3EF", color: "#57534E" }}>
              <Paperclip size={10} />{a.filename}
              <button onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))}><X size={10} /></button>
            </span>
          ))}
        </div>
      )}

      {/* Compose */}
      <div className="px-4 py-3 border-t flex-shrink-0" style={{ borderColor: "#E7E5E4" }}>
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: "#E7E5E4" }}>
          <RichTextEditor content={content} onChange={(v) => {
            setContent(v);
            const now = Date.now();
            if (now - lastTypeSentRef.current > 2000) {
              lastTypeSentRef.current = now;
              send({ type: "typing", thread_id: thread.id, user_name: user?.name || "Agency" });
            }
          }} placeholder="Write a message..." />
          <div className="flex items-center justify-between px-3 py-2 border-t" style={{ borderColor: "#F5F3EF", background: "#FAFAF9" }}>
            <button onClick={() => fileRef.current?.click()} className="p-1.5 rounded-lg hover:bg-stone-100" style={{ color: "#A8A29E" }}>
              <Paperclip size={14} />
            </button>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFile} />
            <Button
              size="sm"
              disabled={sending}
              onClick={handleSend}
              style={{ background: "#292524", color: "#fff" }}
              data-testid="admin-messages-send"
            >
              <Send size={13} className="mr-1.5" />{sending ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Messages() {
  const { subscribe } = useWebSocket();
  const [searchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [mobileView, setMobileView] = useState("list"); // "list" | "conversation"

  const loadThreads = useCallback(() => {
    api.get("/threads")
      .then((r) => {
        setThreads(r.data);
        const deepLinkId = searchParams.get("thread");
        setSelected((prev) => {
          if (prev) return prev;
          if (deepLinkId) return r.data.find((t) => t.id === deepLinkId) || r.data[0] || null;
          return r.data[0] || null;
        });
      })
      .finally(() => setLoading(false));
  }, [searchParams]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // Mark all as read when Messages page is visited
  useEffect(() => {
    api.post("/requests/notifications/mark-all-read").catch(() => {});
  }, []);

  // If deep-link thread id is set, switch to conversation view on mobile
  useEffect(() => {
    if (searchParams.get("thread") && selected) setMobileView("conversation");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // WS: refresh thread list when client sends a message
  useEffect(() => {
    const unsub = subscribe((event) => {
      if (event.type === "new_message" && event.sender_type === "client") {
        api.get("/threads").then((r) => setThreads(r.data)).catch(() => {});
      }
    });
    return unsub;
  }, [subscribe]);

  const handleDelete = (threadId) => {
    setThreads((p) => p.filter((t) => t.id !== threadId));
    setSelected((prev) => (prev?.id === threadId ? null : prev));
    setMobileView("list");
  };

  const handleSelectThread = (t) => {
    setSelected(t);
    setMobileView("conversation");
  };

  const filtered = threads.filter((t) => {
    const matchFilter = filter === "unread" ? t.unread_by_agency : true;
    const matchSearch = !search || t.subject?.toLowerCase().includes(search.toLowerCase()) || t.client_name?.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const unreadCount = threads.filter((t) => t.unread_by_agency).length;

  return (
    <Layout>
      <div className="flex h-full overflow-hidden" data-testid="admin-messages-page">
        {/* Thread list sidebar */}
        <div className={`${mobileView === "list" ? "flex" : "hidden"} md:flex w-full md:w-72 border-r flex-shrink-0 flex-col overflow-hidden`} style={{ borderColor: "#E7E5E4", background: "#fff" }}>
          <div className="px-4 py-4 border-b flex-shrink-0" style={{ borderColor: "#E7E5E4" }}>
            <h1 className="font-serif text-xl font-bold mb-3" style={{ color: "#292524" }}>
              Messages {unreadCount > 0 && <span className="text-xs font-sans font-medium ml-1.5 px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#D97706" }}>{unreadCount}</span>}
            </h1>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations..."
              className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
              style={{ borderColor: "#E7E5E4", background: "#FAFAF9" }}
            />
            <div className="flex gap-1 mt-2">
              {["all", "unread"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className="text-xs px-3 py-1 rounded-full transition-colors capitalize"
                  style={{
                    background: filter === f ? "#292524" : "#F5F3EF",
                    color: filter === f ? "#fff" : "#78716C",
                  }}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {loading ? (
              <p className="text-xs text-center py-8" style={{ color: "#A8A29E" }}>Loading...</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-center py-8" style={{ color: "#A8A29E" }}>No conversations</p>
            ) : filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => handleSelectThread(t)}
                data-testid={`admin-thread-${t.id}`}
                className="w-full text-left px-4 py-3.5 transition-colors"
                style={{
                  background: selected?.id === t.id ? "#F5F3EF" : "transparent",
                  borderBottom: "1px solid #F5F3EF",
                }}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <p className={`text-xs font-medium truncate flex-1 mr-2 ${t.unread_by_agency ? "font-semibold" : ""}`} style={{ color: "#292524" }}>
                    {t.unread_by_agency && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 mb-0.5 flex-shrink-0" style={{ background: "#D97706" }} />}
                    {t.subject}
                  </p>
                  <span className="text-xs flex-shrink-0" style={{ color: "#C7C3BF" }}>{fmt(t.last_message_at)}</span>
                </div>
                <p className="text-xs font-medium mb-0.5" style={{ color: "#D97706" }}>{t.client_name}</p>
                <p className="text-xs truncate" style={{ color: "#A8A29E" }}>{t.last_message_preview || "No messages"}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Conversation area */}
        <div className={`${mobileView === "conversation" ? "flex" : "hidden"} md:flex flex-col flex-1 overflow-hidden`}>
          {/* Mobile back button */}
          {selected && (
            <button onClick={() => setMobileView("list")} className="md:hidden flex items-center gap-2 px-4 py-3 border-b text-sm font-medium" style={{ color: "#57534E", borderColor: "#E7E5E4" }}>
              ← Back to conversations
            </button>
          )}
          {selected ? (
            <Conversation key={selected.id} thread={selected} onDelete={handleDelete} />
          ) : (
            <div className="flex-1 flex items-center justify-center" style={{ color: "#A8A29E" }}>
              <p className="text-sm">Select a conversation to view messages</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
